import {
    BaloiseAddress,
    BaloiseDriverInfo,
    BaloiseGetSimulationTariffsRequest,
    BaloiseVehicleInfo,
    mobilityService,
    SimulationDto,
} from "@lg-cfa/services";
import { getCityName } from "@lg-cfa/utils";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";

import { useFlowNavigation } from "../useFlowNavigation";

import {
    GetTariffParameterObjectCarType,
    PageNames,
    VehicleType,
} from "@enums";
import { driverInformationStreetState } from "@states/userData";
import { UserDataInterface } from "@types";

export function useTariff(data: UserDataInterface) {
    const [simulationDto, setSimulationDto] = useState<
        SimulationDto | undefined
    >(undefined);
    const [tariffLoading, setTariffLoading] = useState<boolean>(false);
    const [propertyStreet] = useRecoilState(driverInformationStreetState);
    const { navigateTo } = useFlowNavigation();

    const getTariff = () => {
        const sanitizeCurrencyInput = (value: string) => {
            let sanitizedCurrency = value.split(",")[0];
            sanitizedCurrency = sanitizedCurrency
                .replaceAll(".", "")
                .replaceAll("€", "")
                .replaceAll(" ", "");
            return Number(sanitizedCurrency);
        };

        const getVehicleInfo = () => {
            const carValue =
                data.carType === GetTariffParameterObjectCarType.NewCar
                    ? sanitizeCurrencyInput(data.carDescriptionCatalogValue)
                    : sanitizeCurrencyInput(data.carDescriptionInvoiceValue);

            const vehicleInfo: BaloiseVehicleInfo = {
                carValue: carValue,
                fuelType: data.carDescriptionFuel,
                kwCount: Number(data.carDescriptionPower),
                model: data.carDescriptionModel,
                make: data.carDescriptionMake,
                safetyFeaturesCount: data.carDetailsSecurityOptions?.length,
                vehicleType: VehicleType.PersonalCar,
                purchaseDate:
                    data.carDescriptionDatePurchased as unknown as Date,
                firstUseDate:
                    data.carDescriptionDateRegistration as unknown as Date,
                vehicleAgeType: data.carType,
                yearlyDistance: Number(data.carDetailsMileage),
            };

            return vehicleInfo;
        };

        const address: BaloiseAddress = {
            city: getCityName(data.driverInformationCity),
            postalCode: Number(data.driverInformationPostalCode),
            street: propertyStreet?.name,
            streetId: propertyStreet?.id,
            houseNumber: data.driverInformationNumber,
            bus: data.driverInformationLetterBox,
        };

        const driverInfo: BaloiseDriverInfo = {
            accidentCount: Number(data.driverHistory_Accidents),
            address: address,
            birthDate: data.driverInformationBirthday as unknown as Date,
            driversLicenseStartDate:
                data.driverHistory_License as unknown as Date,
            driverLast5Years:
                data.driverHistory_Insurance === "0" ? false : true,
        };

        const tariffObject: BaloiseGetSimulationTariffsRequest = {
            driverInfo: driverInfo,
            vehicleInfo: getVehicleInfo(),
        };

        setTariffLoading(true);
        mobilityService
            .baloiseGetSimulationTariffs(tariffObject)
            .then((response) => {
                setSimulationDto(response);
            })
            .catch((error) => {
                console.error(error);
                navigateTo(PageNames.TechnicalDifficulties);
            });
    };

    useEffect(() => {
        getTariff();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (simulationDto && Object.keys(simulationDto).length > 0) {
            setTariffLoading(false);
        } else if (simulationDto && Object.keys(simulationDto).length === 0) {
            navigateTo(PageNames.TechnicalDifficulties);
        }
        // eslint-disable-next-line
    }, [simulationDto]);

    return { simulationDto, tariffLoading };
}
